import { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../lib/utils";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Shield, UserPlus, AlertCircle, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";

export const Setup = () => {
  const [step, setStep] = useState(1);
  const [hasUsers, setHasUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const { login } = useAuth();
  const navigate = useNavigate();

  const [companyData, setCompanyData] = useState({
    name: "",
    address: "",
    phone: "",
  });

  const [adminData, setAdminData] = useState({
    email: "",
    password: "",
    name: "",
  });

  useEffect(() => {
    // Check if setup is needed
    const checkSetup = async () => {
      try {
        const response = await axios.get(`${API_URL}/users`);
        setHasUsers(response.data.length > 0);
      } catch (error) {
        // If 401, no users exist yet
        if (error.response?.status === 401) {
          setHasUsers(false);
        } else {
          setHasUsers(true);
        }
      } finally {
        setLoading(false);
      }
    };
    checkSetup();
  }, []);

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    // Store company data and move to next step
    setStep(2);
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // First create company (without auth)
      const companyRes = await axios.post(`${API_URL}/companies`, companyData);
      const companyId = companyRes.data.id;

      // Then create admin user
      await axios.post(`${API_URL}/auth/register`, {
        ...adminData,
        role: "admin",
        company_id: companyId,
      });

      // Login
      await login(adminData.email, adminData.password);
      toast.success("Configuración completada");
      navigate("/dashboard");
    } catch (error) {
      // If company creation fails because auth is required, we need a different approach
      // For initial setup, the backend should allow unauthenticated company creation
      toast.error(error.response?.data?.detail || "Error en la configuración");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (hasUsers) {
    navigate("/login");
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <Card className="w-full max-w-lg shadow-xl">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mb-4">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <CardTitle className="font-heading text-2xl">
            Configuración Inicial
          </CardTitle>
          <CardDescription>
            Configura Sentinel Pro para tu organización
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Progress */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                step >= 1
                  ? "bg-primary text-white"
                  : "bg-slate-200 text-slate-500"
              }`}
            >
              1
            </div>
            <div className="w-16 h-1 bg-slate-200">
              <div
                className={`h-full bg-primary transition-all ${
                  step >= 2 ? "w-full" : "w-0"
                }`}
              />
            </div>
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                step >= 2
                  ? "bg-primary text-white"
                  : "bg-slate-200 text-slate-500"
              }`}
            >
              2
            </div>
          </div>

          {step === 1 ? (
            <form onSubmit={handleCreateCompany} className="space-y-4">
              <div className="flex items-center gap-2 text-primary mb-4">
                <Building2 className="h-5 w-5" />
                <span className="font-medium">Paso 1: Crear Compañía</span>
              </div>

              <div className="space-y-2">
                <Label htmlFor="company_name">Nombre de la Compañía *</Label>
                <Input
                  id="company_name"
                  value={companyData.name}
                  onChange={(e) =>
                    setCompanyData({ ...companyData, name: e.target.value })
                  }
                  placeholder="Mi Empresa de Seguridad"
                  required
                  data-testid="setup-company-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company_address">Dirección</Label>
                <Input
                  id="company_address"
                  value={companyData.address}
                  onChange={(e) =>
                    setCompanyData({ ...companyData, address: e.target.value })
                  }
                  placeholder="Calle Principal #123"
                  data-testid="setup-company-address"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company_phone">Teléfono</Label>
                <Input
                  id="company_phone"
                  value={companyData.phone}
                  onChange={(e) =>
                    setCompanyData({ ...companyData, phone: e.target.value })
                  }
                  placeholder="787-555-0123"
                  data-testid="setup-company-phone"
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                data-testid="setup-next-btn"
              >
                Siguiente
              </Button>
            </form>
          ) : (
            <form onSubmit={handleCreateAdmin} className="space-y-4">
              <div className="flex items-center gap-2 text-primary mb-4">
                <UserPlus className="h-5 w-5" />
                <span className="font-medium">Paso 2: Crear Administrador</span>
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin_name">Nombre Completo *</Label>
                <Input
                  id="admin_name"
                  value={adminData.name}
                  onChange={(e) =>
                    setAdminData({ ...adminData, name: e.target.value })
                  }
                  placeholder="Juan Pérez"
                  required
                  data-testid="setup-admin-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin_email">Correo Electrónico *</Label>
                <Input
                  id="admin_email"
                  type="email"
                  value={adminData.email}
                  onChange={(e) =>
                    setAdminData({ ...adminData, email: e.target.value })
                  }
                  placeholder="admin@empresa.com"
                  required
                  data-testid="setup-admin-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin_password">Contraseña *</Label>
                <Input
                  id="admin_password"
                  type="password"
                  value={adminData.password}
                  onChange={(e) =>
                    setAdminData({ ...adminData, password: e.target.value })
                  }
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="setup-admin-password"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1"
                >
                  Atrás
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={loading}
                  data-testid="setup-complete-btn"
                >
                  {loading ? "Configurando..." : "Completar"}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
